/* eslint-disable import/prefer-default-export */
import { Column } from "react-table";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import { ExtractedPackingListTableItem } from "api/models/documentProcessor";
import { ReactComponent as PDFIcon } from "assets/images/iconsV2/pdf.svg";
import { ReactComponent as CheckIcon } from "assets/images/iconsV2/check-circle-green.svg";
import { ReactComponent as RefreshIcon } from "assets/images/iconsV2/refresh.svg";
import Badge from "components/v2/Badge/Badge";
import Button from "components/v2/Button/Button";
import LoadingSpinner from "components/v2/LoadingSpinner/LoadingSpinner";
import { formatDate } from "helpers/dateTimeHelper";

interface extractedPackingListColumnsProps {
  onClickViewDraft: (job: ExtractedPackingListTableItem) => void;
  onClickContinue: (job: ExtractedPackingListTableItem) => void;
  onRetryJob: (jobId: number) => void;
  retryExtractedPackingListJobLoading: boolean;
}

export default function useGetExtractedPackingListColumns({
  onClickViewDraft,
  onClickContinue,
  onRetryJob,
  retryExtractedPackingListJobLoading,
}: extractedPackingListColumnsProps) {
  const intl = useIntl();
  const columns: Column<any>[] = [
    {
      Header: intl.formatMessage({ id: "documentParser.uploadPage.tableHeader.fileName", defaultMessage: "File Name" }),
      accessor: (row) => [row.file_name, row.file_url],
      width: 200,
      Cell: ({ value }: any) => (
        <div className="d-flex align-items-center gap-xl">
          <div style={{ width: 36, height: 36 }}>
            <PDFIcon width={36} height={36} />
          </div>
          <a
            href={value[1]}
            target="_blank"
            rel="noreferrer"
            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {value[0]}
          </a>
        </div>
      ),
    },
    {
      Header: intl.formatMessage({
        id: "documentParser.uploadPage.tableHeader.paymentLocation",
        defaultMessage: "Payment Location",
      }),
      accessor: "payment_location_name",
      width: 100,
      Cell: ({ value }: any) => <div className="text-sm">{value}</div>,
    },
    {
      Header: intl.formatMessage({
        id: "documentParser.uploadPage.tableHeader.pageCount",
        defaultMessage: "Page Count",
      }),
      accessor: "page_count",
      width: 80,
      Cell: ({ value }: any) => (
        <div className="text-sm">
          {value > 0 ? (
            <>
              {value}{" "}
              {intl.formatMessage({ id: "documentParser.uploadPage.table.pages", defaultMessage: "pages" }).toLowerCase()}
            </>
          ) : (
            "-"
          )}
        </div>
      ),
    },
    {
      Header: intl.formatMessage({ id: "documentParser.uploadPage.tableHeader.uploadedAt", defaultMessage: "Uploaded At" }),
      accessor: "created_at",
      width: 100,
      Cell: ({ value }: any) => <div className="text-sm">{formatDate(value, "DD MMM YY HH:mm")}</div>,
    },
    {
      Header: intl.formatMessage({ id: "documentParser.uploadPage.tableHeader.status", defaultMessage: "Status" }),
      accessor: "status",
      align: "center",
      width: 180,
      Cell: ({ value, row }: any) => {
        const attempts = row.original?.attempts || 0;
        function renderBadge() {
          switch (value) {
            case "new":
              return null;
            case "processing":
              return (
                <Badge
                  label={intl.formatMessage({
                    id: "documentParser.uploadPage.table.status.processing",
                    defaultMessage: "Processing",
                  })}
                  color="gray"
                  type="status-badge"
                />
              );
            case "ready_for_review":
              return (
                <Badge
                  label={intl.formatMessage({
                    id: "documentParser.uploadPage.table.status.readyForReview",
                    defaultMessage: "Ready for review",
                  })}
                  color="brand"
                  type="status-badge"
                />
              );
            case "pending_conversion":
              return (
                <Badge
                  label={intl.formatMessage({
                    id: "documentParser.uploadPage.table.status.pendingSubmission",
                    defaultMessage: "Pending submission",
                  })}
                  color="warning"
                  type="status-badge"
                />
              );
            case "converted":
              return (
                <Badge
                  label={intl.formatMessage({
                    id: "documentParser.uploadPage.table.status.converted",
                    defaultMessage: "Converted",
                  })}
                  color="success"
                  type="status-badge"
                  icon={<CheckIcon width={14} height={14} />}
                  variant="icon-leading"
                />
              );
            case "failed":
              return attempts >= 3 ? (
                <Badge
                  label={intl.formatMessage({
                    id: "documentParser.uploadPage.table.status.reuploadAndTryAgain",
                    defaultMessage: "Re-upload and try again",
                  })}
                  color="error"
                  type="status-badge"
                />
              ) : (
                <Badge
                  label={intl.formatMessage({
                    id: "documentParser.uploadPage.table.status.failed",
                    defaultMessage: "Failed",
                  })}
                  color="error"
                  type="status-badge"
                />
              );
            default:
              return null;
          }
        }
        return renderBadge();
      },
    },
    {
      Header: intl.formatMessage({ id: "documentParser.uploadPage.tableHeader.actions", defaultMessage: "Actions" }),
      accessor: (row) => [row],
      align: "center",
      width: 120,
      Cell: ({ value }: any) => {
        const job = value[0];
        const { status, request_type, request_reference, attempts, ocr_job_id } = job;
        const navigate = useNavigate();

        const handleClickViewRequest = () => {
          if (request_type === "quickrequest") navigate(`/shipment/${request_reference}/quick`);
          else navigate(`/shipment/${request_reference}`);
        };

        function renderActionButton() {
          switch (status) {
            case "processing":
              return <LoadingSpinner size="24px" />;
            case "failed":
              return attempts >= 3 || retryExtractedPackingListJobLoading ? (
                <Button iconLeft={<RefreshIcon />} variant="link-gray" size="md" disabled />
              ) : (
                <Button iconLeft={<RefreshIcon />} variant="link-gray" size="md" onClick={() => onRetryJob(ocr_job_id)} />
              );
            case "ready_for_review":
              return (
                <Button
                  label={intl.formatMessage({
                    id: "documentParser.uploadPage.table.action.viewDraft",
                    defaultMessage: "View Draft",
                  })}
                  onClick={() => onClickViewDraft(job)}
                  size="sm"
                />
              );
            case "pending_conversion":
              return (
                <Button
                  label={intl.formatMessage({
                    id: "login.continue",
                    defaultMessage: "Continue",
                  })}
                  onClick={() => onClickContinue(job)}
                  size="sm"
                />
              );
            case "converted":
              return <Button label={request_reference} variant="link-color" onClick={handleClickViewRequest} />;
            default:
              return null;
          }
        }

        return <div className="d-flex justify-content-center">{renderActionButton()}</div>;
      },
    },
  ];
  return { columns };
}
